import "./products.css";
import "../services/service.css";
import SEO from "../../SEO";
import NeuralNetCanvas from "../../components/NeuralNetCanvas";

/* ─── SVG icon helper ────────────────────────────────────────────────────── */
const Icon = ({ d, d2, d3, circle, rect, poly, line, ...rest }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    {circle &&
      (Array.isArray(circle) ? (
        circle.map((c, i) => <circle key={i} {...c} />)
      ) : (
        <circle {...circle} />
      ))}
    {rect &&
      (Array.isArray(rect) ? (
        rect.map((r, i) => <rect key={i} {...r} />)
      ) : (
        <rect {...rect} />
      ))}
    {poly &&
      (Array.isArray(poly) ? (
        poly.map((p, i) => <polyline key={i} points={p} />)
      ) : (
        <polyline points={poly} />
      ))}
    {line &&
      (Array.isArray(line) ? (
        line.map((l, i) => <line key={i} {...l} />)
      ) : (
        <line {...line} />
      ))}
    {d && <path d={d} />}
    {d2 && <path d={d2} />}
    {d3 && <path d={d3} />}
  </svg>
);

/* ─── Bento feature cells ─────────────────────────────────────────────────── */
const bentoFeatures = [
  {
    size: "wide",
    accent: true,
    icon: {
      d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71",
      d2: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
    },
    title: "AI Integration Platform",
    desc: "Connect AI services and intelligent automation into business applications and workflows.",
    tags: [
      "AI Service Integration",
      "Workflow Connectivity",
      "API-Based Integration",
      "Business System Integration",
    ],
  },
  {
    size: "med",
    icon: {
      poly: ["23 6 13.5 15.5 8.5 10.5 1 18"],
      d: "M17 6h6v6",
    },
    title: "Workflow Automation",
    desc: "Automate repetitive business processes and operational workflows using AI-powered execution systems.",
  },
  {
    size: "third",
    icon: {
      d: "M12 2L2 7l10 5 10-5-10-5z",
      d2: "M2 17l10 5 10-5",
      d3: "M2 12l10 5 10-5",
    },
    title: "Intelligent Data Processing",
    desc: "Process and organize operational data with AI-assisted handling and structured workflow management.",
  },
  {
    size: "third",
    icon: {
      d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
    },
    title: "AI Assistant Connectivity",
    desc: "Integrate AI assistants into business operations to support users and improve productivity.",
  },
  {
    size: "third",
    icon: {
      rect: [
        { x: "3", y: "3", width: "7", height: "7" },
        { x: "14", y: "3", width: "7", height: "7" },
        { x: "14", y: "14", width: "7", height: "7" },
        { x: "3", y: "14", width: "7", height: "7" },
      ],
    },
    title: "Centralized Operations Management",
    desc: "Manage AI-connected workflows and operational activities from a centralized platform.",
  },
];

/* ─── How it works steps ─────────────────────────────────────────────────── */
const steps = [
  {
    num: "01",
    title: "Connect Your App",
    desc: "Add AI Bridge credentials to your application. A single API key unlocks access to all integrated models, pipelines, and data connectors.",
  },
  {
    num: "02",
    title: "Choose Your Model",
    desc: "Select the right AI model for each task — an LLM for reasoning, an embedding model for search, or a vision model for image understanding.",
  },
  {
    num: "03",
    title: "Configure Pipelines",
    desc: "Attach RAG pipelines, data transformers, and workflow nodes through the visual config editor. No backend code changes required.",
  },
  {
    num: "04",
    title: "Scale with Confidence",
    desc: "AI Bridge handles load balancing, rate limiting, caching, and failover automatically — so your AI features stay fast under any traffic.",
  },
];

/* ─── Use cases ──────────────────────────────────────────────────────────── */
const useCases = [
  {
    icon: {
      d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71",
      d2: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
    },
    title: "AI Workflow Integration",
    desc: "Integrate AI-powered workflows into business systems and operational processes from a unified platform.",
    bullets: [
      "AI workflow integration",
      "Workflow connectivity",
      "Operational management",
    ],
  },
  {
    icon: {
      poly: ["23 6 13.5 15.5 8.5 10.5 1 18"],
      d: "M17 6h6v6",
    },
    title: "Business Process Automation",
    desc: "Automate repetitive business processes and streamline internal operational workflows efficiently.",
    bullets: [
      "Business process automation",
      "Internal operations",
      "Productivity automation",
    ],
  },
  {
    icon: {
      d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
    },
    title: "AI Assistant Integration",
    desc: "Connect AI assistants into operational environments to support users and improve daily productivity.",
    bullets: [
      "AI assistant integration",
      "User support workflows",
      "Productivity support",
    ],
  },
  {
    icon: {
      d: "M12 2L2 7l10 5 10-5-10-5z",
      d2: "M2 17l10 5 10-5",
      d3: "M2 12l10 5 10-5",
    },
    title: "Data Processing Workflows",
    desc: "Process and manage operational data with structured AI-assisted workflows and centralized data handling.",
    bullets: [
      "Data processing workflows",
      "Centralized data management",
      "Structured data handling",
    ],
  },
];

/* ─── Capabilities ────────────────────────────────────────────────────────── */
const capabilities = [
  {
    icon: {
      d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71",
      d2: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
    },
    label: "Simplified AI Adoption",
    sub: "Easier AI integration, faster operational setup and reduced manual workflows",
  },
  {
    icon: {
      poly: ["23 6 13.5 15.5 8.5 10.5 1 18"],
      d: "M17 6h6v6",
    },
    label: "Improved Operational Efficiency",
    sub: "Streamlined workflows, faster task execution and centralized operations",
  },
  {
    icon: {
      rect: [
        { x: "3", y: "3", width: "7", height: "7" },
        { x: "14", y: "3", width: "7", height: "7" },
        { x: "14", y: "14", width: "7", height: "7" },
        { x: "3", y: "14", width: "7", height: "7" },
      ],
    },
    label: "Connected Business Systems",
    sub: "Unified workflow handling, integrated operations and improved data flow",
  },
  {
    icon: {
      d: "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z",
      d2: "M3.27 6.96L12 12.01l8.73-5.05",
      line: { x1: "12", y1: "22.08", x2: "12", y2: "12" },
    },
    label: "Centralized Workflow Management",
    sub: "Manage operational activities and AI-connected systems from one platform",
  },
];

/* ─── Stats ───────────────────────────────────────────────────────────────── */
const stats = [
  { value: "15+", label: "AI Models Supported" },
  { value: "<200ms", label: "First-Token Latency" },
  { value: "99.9%", label: "Uptime SLA" },
  { value: "5 min", label: "Integration Time" },
];

const chartBars = [50, 35, 70, 55, 85, 65, 90];

/* ════════════════════════════════════════════════════════════════════════════
   COMPONENT
   ════════════════════════════════════════════════════════════════════════════ */
export default function AiBridge() {
  return (
    <>
      <SEO
        title="AI Bridge: AI Integration & API Automation | Best Code Technology"
        description="AI Bridge connects AI models and APIs for seamless automation and integration."
        keywords="AI integration, API automation, AI platform"
        url="https://bestcodetechnology.in/products/ai-bridge"
      />

      <div className="uni-page">
        {/* ── HERO ── */}
        <section className="svc-hero svc-hero--neural">
          <NeuralNetCanvas />
          <div className="neural-bg-glow" aria-hidden="true" />
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 text-center">
                <h1 className="svc-hero__headline">
                  Connect AI Systems &amp; <span>Business Workflows</span>
                </h1>
                <p className="svc-hero__desc">
                  AI Bridge is an AI integration platform designed to connect
                  business systems, workflows, applications, and operational
                  processes with AI-powered automation and intelligent data
                  handling.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── BENTO FEATURES ── */}
        <section className="uni-bento">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label">Key Features</span>
                <h2 className="svc-section-title mt-3">
                  Core Features of AI Bridge
                </h2>
                <p className="svc-section-sub">
                  AI Bridge helps businesses integrate AI capabilities into
                  existing systems and operational workflows without rebuilding
                  entire platforms.
                </p>
              </div>
            </div>

            <div className="uni-bento__grid">
              {bentoFeatures.map((f, i) => (
                <div
                  key={i}
                  className={`uni-bento__cell uni-bento__cell--${f.size}${f.accent ? " uni-bento__cell--accent" : ""}`}
                >
                  <div className="uni-bento__cell-icon">
                    <Icon {...f.icon} />
                  </div>
                  <h3 className="uni-bento__cell-title">{f.title}</h3>
                  <p className="uni-bento__cell-desc">{f.desc}</p>
                  {f.tags && (
                    <div className="uni-bento__tags">
                      {f.tags.map((t) => (
                        <span key={t} className="uni-bento__tag">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="uni-steps">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label">How It Works</span>
                <h2 className="svc-section-title mt-3">
                  AI in Your App in Four Simple Steps
                </h2>
                <p className="svc-section-sub">
                  No complex ML infrastructure, no long integration sprints. AI
                  Bridge is designed to be production-ready from day one.
                </p>
              </div>
            </div>

            <div className="row g-4 uni-steps__row">
              {steps.map((s) => (
                <div key={s.num} className="col-12 col-sm-6 col-lg-3">
                  <div className="uni-step">
                    <div className="uni-step__num">{s.num}</div>
                    <h4 className="uni-step__title">{s.title}</h4>
                    <p className="uni-step__desc">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── USE CASES ── */}
        <section className="uni-cases">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label">Use Cases</span>
                <h2 className="svc-section-title mt-3">AI Bridge Use Cases</h2>
                <p className="svc-section-sub">
                  AI Bridge can be used across multiple business operations and
                  workflow environments.
                </p>
              </div>
            </div>

            <div className="row g-4">
              {useCases.map((uc, i) => (
                <div key={i} className="col-12 col-md-6 col-xl-3">
                  <div className="uni-case-card">
                    <div className="uni-case-card__icon">
                      <Icon {...uc.icon} />
                    </div>
                    <h4 className="uni-case-card__title">{uc.title}</h4>
                    <p className="uni-case-card__desc">{uc.desc}</p>
                    <ul className="uni-case-card__list">
                      {uc.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CAPABILITIES ── */}
        <section className="uni-capabilities">
          <div className="container-xl">
            <div className="row align-items-center g-5">
              <div className="col-12 col-lg-5">
                <span className="pill-label">Advanced Capabilities</span>
                <h2 className="svc-section-title mt-3">
                  Why Businesses Use AI Bridge
                </h2>
                <p className="svc-section-sub svc-section-sub--left">
                  AI Bridge helps organizations simplify AI adoption and connect
                  intelligent automation with daily operations.
                </p>
              </div>

              <div className="col-12 col-lg-7">
                <div className="uni-cap-visual">
                  {capabilities.map((cap, i) => (
                    <div key={i} className="uni-cap-row">
                      <div className="uni-cap-row__icon">
                        <Icon {...cap.icon} />
                      </div>
                      <div>
                        <p className="uni-cap-row__label">{cap.label}</p>
                        <p className="uni-cap-row__sub">{cap.sub}</p>
                      </div>
                      <span className="uni-cap-row__badge">Included</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
